import { html, css, LitElement } from "lit";
import { customElement } from "lit/decorators.js";
import { state } from "lit/decorators.js";
import { hermesClient, HERMES_BASE_URL } from "../hermes-singleton.js";

@customElement("ead-chat-minimal")
export class EadChatMinimal extends LitElement {
  static styles = css`
    :host { display: block; height: 100%; }
    .container { display: flex; flex-direction: column; height: 100%; padding: 20px; padding-bottom: 80px; }
    .messages { flex: 1; overflow-y: auto; margin-bottom: 20px; min-height: 0; }
    .message { padding: 10px; margin: 8px 0; border-radius: 8px; background: #222326; }
    .input-area {
      display: flex;
      gap: 12px;
      padding: 16px;
      background: var(--bg-surface, #222326);
      border-radius: 12px;
      box-shadow: 0 -2px 8px rgba(0,0,0,0.2);
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 1000;
    }
    input {
      flex: 1;
      padding: 14px 18px;
      border-radius: 24px;
      border: 2px solid #2e2f33;
      background: #1a1b1e;
      color: #e4e5e7;
      font-size: 14px;
      pointer-events: auto !important;
      transition: border-color 0.2s;
    }
    input:focus {
      outline: none;
      border-color: #6ea8fe;
      box-shadow: 0 0 0 3px rgba(110, 168, 254, 0.1);
    }
    button {
      padding: 14px 28px;
      border-radius: 24px;
      border: none;
      background: #6ea8fe;
      color: white;
      cursor: pointer;
      font-weight: 600;
      font-size: 14px;
      transition: background 0.2s;
    }
    button:hover {
      background: #5a9aef;
    }
    button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .debug {
      padding: 10px;
      background: rgba(110, 168, 254, 0.1);
      border-radius: 6px;
      margin: 10px 0;
      font-size: 12px;
      font-family: monospace;
    }
  `;

  @state() private _messages: Array<{role: string, content: string}> = [];
  @state() private _inputValue = "";
  @state() private _sending = false;
  @state() private _sessionKey = "main-chat-session";
  @state() private _debug = "";

  private _handleInput(e: Event) {
    const target = e.target as HTMLInputElement;
    this._inputValue = target.value;
    this._debug = `Input: "${target.value}" | Length: ${target.value.length}`;
    console.log('[Minimal Chat] Input:', target.value);
  }

  private _handleKeyDown(e: KeyboardEvent) {
    this._debug = `Key: "${e.key}" | Value: "${this._inputValue}"`;
    console.log('[Minimal Chat] Keydown:', e.key, this._inputValue);
    if (e.key === "Enter") {
      e.preventDefault();
      this._sendMessage();
    }
  }

  async connectedCallback() {
    super.connectedCallback();
    console.log('[Minimal Chat] Initializing...');
    await this._initSession();
  }

  private async _initSession() {
    try {
      console.log('[Minimal Chat] Creating session...');
      const result = await hermesClient.request<{
        session_id: string;
        session_key: string;
      }>("sessions.create", {
        key: this._sessionKey,
        label: "Main Chat"
      });

      if (result?.session_id) {
        console.log('[Minimal Chat] Session created:', result);

        // Load existing messages
        await this._loadHistory();
      }
    } catch (error) {
      console.error('[Minimal Chat] Session creation failed:', error);
    }
  }

  private async _loadHistory() {
    try {
      console.log('[Minimal Chat] Loading history for session:', this._sessionKey);

      // Try to fetch directly to see what URL is being used
      const directUrl = `${HERMES_BASE_URL}/v1/chat/history?session_key=${this._sessionKey}&limit=50`;
      console.log('[Minimal Chat] Direct URL would be:', directUrl);

      const result = await hermesClient.request<{ messages: Array<{role: string, content: string; label?: string}> }>("chat.history", {
        sessionKey: this._sessionKey,
        limit: 50,
      });

      console.log('[Minimal Chat] History result:', result);

      if (result?.messages) {
        // Filter and clean messages
        const cleanedMessages = result.messages
          .filter(msg => {
            // Only show user/assistant messages
            if (msg.role !== 'user' && msg.role !== 'assistant') return false;
            // Filter out empty messages
            if (!msg.content || msg.content.trim() === '') return false;
            // Filter out tool call messages (they start with "tool:")
            if (msg.content.startsWith('tool:')) return false;
            return true;
          })
          .filter((msg, index, self) => {
            // Remove duplicates while keeping order
            const firstIndex = self.findIndex((m) => m.content === msg.content && m.role === msg.role);
            return firstIndex === index;
          });

        this._messages = cleanedMessages;
        console.log('[Minimal Chat] Messages loaded:', this._messages.length, 'cleaned from', result.messages.length);
      }
    } catch (error) {
      console.error('[Minimal Chat] Failed to load history:', error);
    }
  }

  private async _sendMessage() {
    if (!this._inputValue.trim() || this._sending) return;

    const userMessage = this._inputValue.trim();
    this._sending = true;

    console.log('[Minimal Chat] Sending message:', userMessage);

    // Add user message immediately
    this._messages = [
      ...this._messages,
      { role: "user", content: userMessage }
    ];

    const currentInputValue = this._inputValue;
    this._inputValue = "";

    try {
      console.log('[Minimal Chat] Calling chat.send...');
      const response = await hermesClient.request("chat.send", {
        sessionKey: this._sessionKey,
        content: currentInputValue,
        deliver: true,
      });

      console.log('[Minimal Chat] Send response:', response);
      console.log('[Minimal Chat] Response type:', typeof response);
      console.log('[Minimal Chat] Response keys:', response ? Object.keys(response) : 'response is null/undefined');

      // Wait for the AI to respond (DeepSeek is fast but let's be safe)
      console.log('[Minimal Chat] Waiting 3 seconds for AI response...');
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Load the updated history
      console.log('[Minimal Chat] Loading history...');
      await this._loadHistory();

      console.log('[Minimal Chat] Final messages count:', this._messages.length);
      this._sending = false;

    } catch (error) {
      console.error('[Minimal Chat] Send error:', error);

      // Only add error message if there isn't one already
      const lastMessage = this._messages[this._messages.length - 1];
      if (!lastMessage || lastMessage.role !== 'system' || !lastMessage.content.includes('Error')) {
        this._messages = [
          ...this._messages,
          { role: "system", content: `⚠️ Error: ${error instanceof Error ? error.message : 'Failed to send message'}` }
        ];
      }

      this._sending = false;
    }
  }

  render() {
    return html`
      <div class="container">
        <div class="messages">
          ${this._messages.map(msg => html`
            <div class="message" style="background: ${msg.role === 'user' ? 'rgba(110, 168, 254, 0.2)' : msg.role === 'assistant' ? 'rgba(76, 175, 80, 0.2)' : '#222326'}">
              <div style="font-size: 11px; color: #8b8d93; margin-bottom: 4px;">
                ${msg.role === 'user' ? '👤 You' : msg.role === 'assistant' ? '🤖 Assistant' : '🔧 System'}
              </div>
              <div style="word-break: break-word; line-height: 1.5;">${msg.content}</div>
            </div>
          `)}
          ${this._messages.length === 0 ? html`
            <div style="text-align: center; color: #8b8d93; margin-top: 50px;">
              <div style="font-size: 40px; margin-bottom: 20px;">💬</div>
              <div style="font-size: 16px; font-weight: 500; margin-bottom: 8px;">Start a conversation</div>
              <div style="font-size: 13px; color: #8b8d93;">Type your message below</div>
            </div>
          ` : ''}
          ${this._sending ? html`
            <div style="text-align: center; color: #8b8d93; padding: 10px;">
              <div style="display: inline-block; width: 20px; height: 20px; border: 2px solid #2e2f33; border-top-color: #6ea8fe; border-radius: 50%; animation: spin 1s linear infinite; margin-right: 8px;"></div>
              <span style="font-size: 13px;">AI is thinking...</span>
            </div>
            <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
          ` : ''}
        </div>
        <div class="input-area">
          <input
            type="text"
            placeholder="Type your message here..."
            .value=${this._inputValue}
            @input=${this._handleInput}
            @keydown=${this._handleKeyDown}
          />
          <button
            ?disabled=${!this._inputValue.trim() || this._sending}
            @click=${this._sendMessage}
          >
            ${this._sending ? '...' : 'Send'}
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ead-chat-minimal": EadChatMinimal;
  }
}